import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    flexRender,
    createColumnHelper,
    ColumnOrderState,
} from '@tanstack/react-table';
import { Search, ArrowUpDown, ThumbsUp, ThumbsDown, Eye, GripVertical } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { ParkingLot } from '../lib/types';
import { calculateFeedbackSummary } from '../lib/utils';

const columnHelper = createColumnHelper<ParkingLot>();

interface ParkingLotTableProps {
    parkingLots: ParkingLot[];
    isLoading: boolean;
    error: any;
    onViewDetails?: (parkingLot: ParkingLot) => void;
    onSearch?: (searchTerm: string) => void;
    searchTerm?: string;
    onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
    currentSortBy?: string;
    currentSortOrder?: 'asc' | 'desc';
}

export const ParkingLotTable: React.FC<ParkingLotTableProps> = ({
    parkingLots,
    isLoading,
    error,
    onViewDetails,
    onSearch,
    searchTerm = '',
    onSort,
    currentSortBy = 'address',
    currentSortOrder = 'asc',
}) => {
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([
        'address',
        'lotId',
        'spaces',
        'parking_type',
        'displayInApp',
        'additionalProperties.cleaned_price_zone',
        'additionalProperties.free_duration_rules',
        'additionalProperties.cleaned_notes',
        'feedback',
        'actions'
    ]);

    const [debouncedSearchTerm] = useDebounce(localSearchTerm, 500);

    // Handle search input changes
    const handleSearchChange = useCallback((value: string) => {
        setLocalSearchTerm(value);
    }, []);

    // Handle Enter key press for immediate search
    const handleSearchKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (onSearch) {
                onSearch(localSearchTerm);
            }
        }
    }, [onSearch, localSearchTerm]);

    // Update local search term when prop changes
    useEffect(() => {
        setLocalSearchTerm(searchTerm);
    }, [searchTerm]);

    // Trigger search when debounced term changes
    useEffect(() => {
        if (onSearch && debouncedSearchTerm !== searchTerm) {
            onSearch(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm, onSearch, searchTerm]);

    // Handle column sorting
    const handleSort = useCallback((columnId: string) => {
        if (onSort) {
            const newSortOrder = currentSortBy === columnId && currentSortOrder === 'asc' ? 'desc' : 'asc';
            onSort(columnId, newSortOrder);
        }
    }, [onSort, currentSortBy, currentSortOrder]);

    const columns = useMemo(
        () => [
            columnHelper.accessor('address', {
                header: 'Address',
                cell: (info) => (
                    <div className="font-medium max-w-[120px] md:max-w-xs truncate" title={info.getValue()}>
                        {info.getValue() || 'N/A'}
                    </div>
                ),
            }),
            columnHelper.accessor('lotId', {
                header: 'Lot ID',
                cell: (info) => (
                    <div className="text-sm font-mono max-w-[80px] md:max-w-xs truncate" title={info.getValue()}>
                        {info.getValue() || 'N/A'}
                    </div>
                ),
            }),
            columnHelper.accessor('spaces', {
                header: 'Spaces',
                cell: (info) => (
                    <div className="text-sm">
                        {info.getValue() || 'N/A'}
                    </div>
                ),
            }),
            columnHelper.accessor('parking_type', {
                header: 'Type',
                cell: (info) => (
                    <div className="text-sm">
                        {info.getValue() || 'N/A'}
                    </div>
                ),
            }),
            columnHelper.accessor('displayInApp', {
                header: 'App',
                cell: (info) => {
                    const displayInApp = info.getValue();
                    if (displayInApp === false) {
                        return (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title="Hidden from public app">
                                Hidden
                            </span>
                        );
                    }
                    return <span className="text-xs text-muted-foreground">—</span>;
                },
            }),
            columnHelper.accessor('additionalProperties.cleaned_price_zone', {
                header: 'Price Zone',
                cell: (info) => (
                    <div className="text-sm max-w-[100px] md:max-w-xs truncate" title={info.getValue()}>
                        {info.getValue() || 'N/A'}
                    </div>
                ),
            }),
            columnHelper.accessor('additionalProperties.free_duration_rules', {
                header: 'Free Duration',
                cell: (info) => (
                    <div className="text-sm text-muted-foreground max-w-[100px] md:max-w-xs truncate" title={info.getValue()}>
                        {info.getValue() || 'N/A'}
                    </div>
                ),
            }),
            columnHelper.accessor('additionalProperties.cleaned_notes', {
                header: 'Notes',
                cell: (info) => (
                    <div className="text-sm text-muted-foreground max-w-[100px] md:max-w-xs truncate" title={info.getValue()}>
                        {info.getValue() || 'N/A'}
                    </div>
                ),
            }),
            columnHelper.accessor('feedback', {
                header: 'Feedback',
                cell: (info) => {
                    const feedback = info.getValue();
                    const summary = calculateFeedbackSummary(feedback);
                    return (
                        <div className="flex items-center gap-1 md:gap-2">
                            <div className="flex items-center gap-1">
                                <ThumbsUp className="h-3 w-3 text-green-600" />
                                <span className="text-xs">{summary.thumbsUp}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <ThumbsDown className="h-3 w-3 text-red-600" />
                                <span className="text-xs">{summary.thumbsDown}</span>
                            </div>
                            {summary.total > 0 && (
                                <span className="text-xs text-muted-foreground hidden md:inline">
                                    ({summary.positivePercentage}%)
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: 'actions',
                header: 'Actions',
                cell: (info) => (
                    <button
                        onClick={() => onViewDetails?.(info.row.original)}
                        className="flex items-center gap-1 md:gap-2 px-2 py-1 md:px-3 md:py-1 text-xs md:text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    >
                        <Eye className="h-3 w-3 md:h-4 md:w-4" />
                        <span className="hidden sm:inline">View</span>
                    </button>
                ),
            }),
        ],
        [onViewDetails]
    );

    const table = useReactTable({
        data: parkingLots,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            columnOrder,
        },
        onColumnOrderChange: setColumnOrder,
        columnResizeMode: 'onChange',
    });

    if (isLoading) {
        return (
            <div className="bg-card border rounded-lg p-6 md:p-8">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-primary"></div>
                    <span className="ml-2 text-muted-foreground text-sm md:text-base">Loading parking lots...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-card border rounded-lg p-6 md:p-8">
                <div className="text-center text-red-600">
                    <p className="text-sm md:text-base">Error loading parking lots: {error.message}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card border rounded-lg">
            {/* Search Header */}
            <div className="p-4 md:p-6 border-b">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <h2 className="text-lg md:text-xl font-semibold">Parking Lots</h2>
                    <div className="relative w-full md:w-auto">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search parking lots..."
                            value={localSearchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            className="w-full md:w-80 pl-10 pr-4 py-2 border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} className="border-b bg-muted/50">
                                {headerGroup.headers.map((header) => {
                                    const columnId = header.column.id;
                                    const isSortable = ['address', 'lotId', 'spaces', 'parking_type'].includes(columnId);
                                    const isSorted = currentSortBy === columnId;

                                    return (
                                        <th
                                            key={header.id}
                                            className="px-3 py-3 md:px-6 md:py-3 text-left text-xs md:text-sm font-medium text-muted-foreground relative group whitespace-nowrap"
                                            style={{ width: header.getSize() }}
                                        >
                                            <div className="flex items-center gap-1 md:gap-2">
                                                <div
                                                    className="cursor-move opacity-0 group-hover:opacity-100 transition-opacity hidden md:block"
                                                    onMouseDown={header.getResizeHandler()}
                                                    onTouchStart={header.getResizeHandler()}
                                                >
                                                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                                                </div>
                                                <div
                                                    className={`flex items-center gap-1 md:gap-2 ${isSortable ? 'cursor-pointer select-none' : ''}`}
                                                    onClick={isSortable ? () => handleSort(columnId) : undefined}
                                                >
                                                    <span className="truncate">{flexRender(header.column.columnDef.header, header.getContext())}</span>
                                                    {isSortable && (
                                                        <div className="flex flex-col">
                                                            <ArrowUpDown
                                                                className={`h-3 w-3 ${isSorted ? 'text-blue-600' : 'text-gray-400'}`}
                                                            />
                                                            {isSorted && (
                                                                <span className="text-xs text-blue-600">
                                                                    {currentSortOrder === 'asc' ? '↑' : '↓'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length > 0 ? (
                            table.getRowModel().rows.map((row) => (
                                <tr key={row.id} className="border-b hover:bg-muted/50">
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-3 py-3 md:px-6 md:py-4">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-6 md:px-6 md:py-8 text-center text-muted-foreground text-sm md:text-base">
                                    {searchTerm ? `No parking lots found matching "${searchTerm}"` : 'No parking lots found'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}; 